import {
  Grid,
  TextField,
  IconButton,
  Box,
  MenuItem,
  Skeleton,
  CircularProgress,
} from '@mui/material';
import RightChevronIcon from 'components/SVG/RightChevronIcon';
import ShareIcon09 from 'components/SVG/ShareIcon09';
import type { Workspace } from 'types/Workspace';
import { InfoCard, SparkImageLoader } from 'sharedComponents/UI';
import { useState } from 'react';
import { useDebounce } from 'hooks/common';
import { WorkSpaceIcon } from 'components/SVG';
import SparkInfiniteScroll from 'sharedComponents/UI/SparkInfiniteScroll';

type Props = {
  onBack: () => void;
  onAddWorkspaceAttachment: (workspace: Workspace) => void;
  WorkspaceHandleLatElementView: () => void;
  isSearchingForWorkspaces?: boolean;
  data?: Workspace[];
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
};

const WorkspacePopoverSkeleton = (
  <>
    {[...Array(5)].map((_, index) => (
      <MenuItem key={index}>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          width='100%'
        >
          <Box alignItems='center' display='flex' gap={1}>
            <Skeleton variant='circular' width={30} height={30} />
            <Skeleton variant='text' width={120} height={20} />
          </Box>
          <Skeleton variant='rectangular' width={18} height={18} />
        </Box>
      </MenuItem>
    ))}
  </>
);

const WorkspacesPopover = ({
  onBack,
  onAddWorkspaceAttachment,
  WorkspaceHandleLatElementView,
  isSearchingForWorkspaces = false,
  data = [],
  isFetchingNextPage = false,
  hasNextPage = false,
}: Props) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hoveredItemIndex, setHoveredItemIndex] = useState<number | null>(null);

  // const { workspaces = [], isLoading } = useWorkspaceSearch(searchTerm, 1);

  const filteredWorkspaces = data.filter((item) =>
    item.attributes.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const debounce = useDebounce();
  const changeSearchTerm = debounce(setSearchTerm, 500);

  const handleChangeSearchTerm = (e: React.ChangeEvent<HTMLInputElement>) => {
    changeSearchTerm(e.target.value);
  };

  return (
    <Grid container direction='column' minWidth={'300px'}>
      <Grid item>
        <Box
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Box display='flex' alignItems='center' gap={1} px={2}>
            <IconButton
              edge='start'
              size='small'
              onClick={onBack}
              sx={{ paddingLeft: 0 }}
            >
              <RightChevronIcon />
            </IconButton>
            <TextField
              placeholder='Search Workspaces'
              variant='standard'
              size='small'
              fullWidth
              onChange={handleChangeSearchTerm}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              InputProps={{ disableUnderline: true }}
              sx={{
                '& .MuiInputBase-input': {
                  padding: 0,
                },
              }}
            />
          </Box>

          <Box
            sx={{
              height: '2px',
              backgroundColor:
                isFocused || isHovered ? '#6267AC' : 'rgba(0, 0, 0, 0.12)',
              transition: 'background-color 0.2s ease',
              my: 0.5,
            }}
          />
        </Box>
      </Grid>

      <SparkInfiniteScroll
        onLastElementView={WorkspaceHandleLatElementView}
        containerStyles={{
          maxHeight: '20rem',
          minHeight: '400px',
          maxWidth: '300px',
          display: 'block',
        }}
        childContainerProps={{ width: '100%' }}
        isLoading={isSearchingForWorkspaces}
        isFetchingNextPage={isFetchingNextPage || hasNextPage}
        loadingContent={WorkspacePopoverSkeleton}
      >
        {!isSearchingForWorkspaces && filteredWorkspaces.length === 0 && (
          <Box p={1}>
            <InfoCard
              customIcon={
                isFetchingNextPage || hasNextPage ? (
                  <CircularProgress size={18} />
                ) : null
              }
              type={isFetchingNextPage || hasNextPage ? 'info' : 'error'}
              info={
                isFetchingNextPage || hasNextPage
                  ? 'Finding Workspaces'
                  : 'No Workspaces found.'
              }
            />
          </Box>
        )}

        {filteredWorkspaces.map((workspace, index) => (
          <MenuItem
            key={workspace.id}
            onClick={() => onAddWorkspaceAttachment(workspace)}
            onMouseEnter={() => setHoveredItemIndex(index)}
            onMouseLeave={() => setHoveredItemIndex(null)}
          >
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              width='100%'
              gap={2}
            >
              <Box alignItems='center' display='flex' gap={1}>
                <Box sx={{ display: 'flex' }}>
                  {workspace?.attributes.imageURL ? (
                    <SparkImageLoader
                      src={workspace?.attributes.imageURL}
                      alt={workspace?.attributes.imageURL}
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '6px',
                      }}
                      containerName='public-assets'
                    />
                  ) : (
                    <WorkSpaceIcon width='24' height='24' />
                  )}
                </Box>

                {workspace.attributes.name}
              </Box>
              {hoveredItemIndex === index && (
                <Box
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`/settings/workspaces/${workspace.id}`);
                  }}
                >
                  <ShareIcon09 width='18' height='18' />
                </Box>
              )}
            </Box>
          </MenuItem>
        ))}
      </SparkInfiniteScroll>
    </Grid>
  );
};

export default WorkspacesPopover;
